import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { STEPS } from 'constants/mercadopago';
import { loadingActions } from 'modules/loading';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import CardSelector from './components/CardSelector';
import CvvModal from './components/CvvModal';
import PaymentForm from './components/PaymentForm';
import CardEditForm from './components/CardEditForm';
import DeleteCardModal from './components/DeleteCardModal';
import PaymentSummary from './components/PaymentSummary';
import Challenge3DS from './components/Challenge3DS';
import { useMercadoPagoFlow } from './hooks/useMercadoPagoFlow';
import './styles.scss';

/**
 * Componente principal de Mercado Pago usando Core Methods.
 * Flujo: Formulario/Tarjetas -> Resumen -> Procesar Pago (o Challenge 3DS).
 * Los iframes de MP se limpian con cleanupAll en handleBack y en goToSummaryWithError.
 * Documentación: https://www.mercadopago.com.mx/developers/es/docs/checkout-api-orders/payment-integration/cards
 */
const MercadoPago = ({
  amount = 0,
  type = '',
  id = '',
  payment,
  orders = [],
  isMultiple = false,
  coverage = 0,
  coverageId = null,
  onClose = () => {},
  onSuccess = () => {},
  onPending = null,
}) => {
  const dispatch = useDispatch();
  // Igual que BBVA: amount = capital; total a mostrar/cobrar = amount + coverage
  const totalAmountDisplay = isMultiple ? amount : (amount || 0) + (coverage || 0);
  const {
    literals,
    step,
    cards,
    selectedCard,
    editingCard,
    showCvvModal,
    showDeleteModal,
    cardToDelete,
    deletingCard,
    setShowCvvModal,
    paymentData,
    loading,
    loadingCards,
    error,
    warning,
    success,
    challengeData,
    recommendOtherMethod,
    handleUseOtherPaymentMethod,
    user,
    handleCardSelect,
    handleAddNewCard,
    handleEditCard,
    handleCancelEdit,
    handleDeleteCard,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleUpdateCard,
    handleCvvSubmit,
    handlePaymentReady,
    handleConfirmPayment,
    handleBack,
    handleChallengeComplete,
    handleChallengeError,
    handleSuccessClose,
    setStepFormOrCards,
  } = useMercadoPagoFlow({
    amount,
    type,
    id,
    payment,
    orders,
    isMultiple,
    coverage,
    coverageId,
    onClose,
    onSuccess,
    onPending,
  });

  useEffect(() => {
    if (step === STEPS.PROCESSING && loading) {
      dispatch(loadingActions.show());
      return () => dispatch(loadingActions.hide());
    }
    return undefined;
  }, [step, loading, dispatch]);

  if (loadingCards || step === STEPS.LOADING) {
    return (
      <Box>
        <div className='text-center py-6'>
          <p>{literals.loading}</p>
        </div>
      </Box>
    );
  }

  if (step === STEPS.PROCESSING && loading) {
    return null;
  }

  return (
    <>
      {step === STEPS.CARDS && (
        <Box>
          <div className='mercadopago-container'>
            <h3 className='mb-4'>{literals.title}</h3>
            <CardSelector
              cards={cards}
              onCardSelect={handleCardSelect}
              onCardEdit={handleEditCard}
            />
            <div className='mt-4'>
              <Button
                text={literals.addCardButton}
                type='outline-primary'
                className='w-full'
                onClick={handleAddNewCard}
              />
            </div>
          </div>
        </Box>
      )}

      {step === STEPS.EDIT_CARD && editingCard && (
        <Box>
          <div className='mercadopago-container'>
            <CardEditForm
              card={editingCard}
              onUpdate={handleUpdateCard}
              onCancel={handleCancelEdit}
              onDelete={handleDeleteCard}
            />
          </div>
        </Box>
      )}

      {step === STEPS.FORM && (
        <PaymentForm
          amount={totalAmountDisplay}
          onPaymentReady={handlePaymentReady}
          onCancel={cards.length > 0 ? setStepFormOrCards : undefined}
        />
      )}

      {step === STEPS.SUMMARY && paymentData && (
        <PaymentSummary
          paymentData={paymentData}
          orders={orders}
          isMultiple={isMultiple}
          error={error}
          warning={warning}
          loading={loading}
          recommendOtherMethod={recommendOtherMethod}
          onUseOtherPaymentMethod={handleUseOtherPaymentMethod}
          onConfirm={handleConfirmPayment}
          onBack={handleBack}
        />
      )}

      {step === STEPS.CHALLENGE && challengeData && (
        <Box>
          <Challenge3DS
            threeDsInfo={challengeData.threeDsInfo}
            userInfo={user ? { id: user.id, email: user.email } : undefined}
            paymentId={challengeData.paymentId}
            onChallengeComplete={handleChallengeComplete}
            onError={handleChallengeError}
          />
        </Box>
      )}

      {showCvvModal && selectedCard && (
        <CvvModal
          card={selectedCard}
          onClose={() => setShowCvvModal(false)}
          onSubmit={handleCvvSubmit}
        />
      )}

      {showDeleteModal && cardToDelete && (
        <DeleteCardModal
          card={cardToDelete}
          onConfirm={handleConfirmDelete}
          onClose={handleCloseDeleteModal}
          loading={deletingCard}
        />
      )}

      {success && (
        <SuccessModal
          title={literals.success.title}
          description={literals.success.description}
          onClose={handleSuccessClose}
        />
      )}
    </>
  );
};

MercadoPago.propTypes = {
  amount: PropTypes.number.isRequired,
  type: PropTypes.string,
  id: PropTypes.string,
  payment: PropTypes.object,
  orders: PropTypes.array,
  isMultiple: PropTypes.bool,
  coverage: PropTypes.number,
  coverageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onPending: PropTypes.func,
};

export default MercadoPago;
